import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import './StickyPanel.scss';

const StickyPanel = ({ controls, children }) => {
	const contentRef = useRef(null);

	const [isScrollable, setIsScrollable] = useState(false);
	const [isAtBottom, setIsAtBottom] = useState(true);
	const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

	const checkScrollState = () => {
		const el = contentRef.current;
		if (!el) return;

		const scrollable = el.scrollHeight > el.clientHeight;
		const atBottom = Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight;

		setIsScrollable(scrollable);
		setIsAtBottom(atBottom);
	};

	const checkKeyboardState = () => {
		const vv = window.visualViewport;
		if (!vv) return;

		const KEYBOARD_THRESHOLD = 80;

		const keyboardOpen = window.innerHeight - vv.height > KEYBOARD_THRESHOLD;

		setIsKeyboardOpen(keyboardOpen);
	}

	useEffect(() => {
		const el = contentRef.current;
		if (!el) return;

		checkScrollState();
		checkKeyboardState();

		el.addEventListener('scroll', checkScrollState);

		const resizeObserver = new ResizeObserver(() => {
			checkScrollState();
			checkKeyboardState();
		});
		resizeObserver.observe(el);

		const vv = window.visualViewport;
		if (vv) {
			vv.addEventListener('scroll', checkKeyboardState);
			vv.addEventListener('resize', checkKeyboardState);
		}

		window.addEventListener('resize', checkKeyboardState);

		return () => {
			el.removeEventListener('scroll', checkScrollState);
			resizeObserver.disconnect();

			if (vv) {
				vv.removeEventListener('scroll', checkKeyboardState);
				vv.removeEventListener('resize', checkKeyboardState);
			}

			window.removeEventListener('resize', checkKeyboardState);
		};
	}, []);

	const showBorder = isScrollable && !isAtBottom;

	return (
		<div className='StickyPanel'>
			<div className='StickyPanel__content' ref={contentRef}>
				{children}
			</div>
			<div
				className={classNames('StickyPanel__footer', {
					'with-border': showBorder,
					'keyboard-open': isKeyboardOpen,
				})}
			>
				{controls}
			</div>
		</div>
	);
};

export default StickyPanel;
